import { Checkbox, Popover, MenuItem, Divider } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import Icon from '@material-ui/core/Icon';
import Tooltip from '@material-ui/core/Tooltip';
import ListAltSharpIcon from '@material-ui/icons/ListAltSharp';
import ScheduleIcon from '@material-ui/icons/Schedule';
import ThumbDownAltSharpIcon from '@material-ui/icons/ThumbDownAltSharp';
import ThumbUpAltSharpIcon from '@material-ui/icons/ThumbUpAltSharp';
import MyTable, { MyTableBody, MyTableCol, MyTableHeader, MyTableRow, MyTablePagination } from 'components/MyTable';
import PermissionHide from 'components/PermissionHide';
import moment from 'moment';
import React, { useCallback, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useObservable } from 'react-use-observable';
import MyTableEmpty from 'v2/components/Shared/MyTable/MyTableEmpty';
import MyTableLoading from 'v2/components/Shared/MyTable/MyTableLoading';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import refundService from 'v2/services/requestManagement/refund';
import tableService from 'v2/services/table';

import type { IRefund } from 'v2/interfaces/requestManagement/refund';
import type { IOrderBy } from 'v2/services/requestManagement/refund';

import useStyle from '../styles';

import type { IMyTableHeaderIndex } from 'components/MyTable/MyTableHeader';

const RefundList = () => {
  const classes = useStyle();
  useObservable(() => refundService.loadRefunds(), []);
  const [isLoading] = useObservable(() => tableService.isLoading(), []);

  const [list] = useObservable(() => {
    return refundService.list();
  }, []);

  const [pagination] = useObservable(() => {
    return refundService.getPaginationResponse();
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentRefund, setCurrentRefund] = useState<IRefund>(null);
  const [ordering] = useObservable(() => refundService.getOrdering(), []);
  const [ordered] = useObservable(() => refundService.getOrdered(), []);
  const onCheckAll = useCallback(() => refundService.checkAllRefunds(), []);
  const onUnCheckAll = useCallback(() => refundService.unCheckAllRefunds(), []);
  const onCheck = useCallback((itemId: string) => refundService.checkRefund(itemId), []);
  const refundChecked = (list || []).filter((o) => o.checked);
  const qtRefundChecked = refundChecked.length;
  const qtRefund = (list || []).length;

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>, refund: IRefund) => {
    setAnchorEl(event.currentTarget);
    setCurrentRefund(refund);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const openDetails = useCallback(() => {
    setAnchorEl(null);
    refundService.openDetails(currentRefund);
  }, [currentRefund]);

  const openReproveMotive = useCallback(() => {
    setAnchorEl(null);
    refundService.openReproveMotive(currentRefund);
  }, [currentRefund]);

  const openApprove = useCallback(() => {
    setAnchorEl(null);
    refundService.openApprove(currentRefund);
  }, [currentRefund]);

  const onChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const per_page = Number(e.target.value);
    refundService.setPagination({ ...pagination, ...{ per_page, current_page: 1 } });
  };

  const onChangePage = (_: unknown, page: number) => {
    const current_page = page + 1;
    refundService.setPagination({ ...pagination, ...{ current_page } });
  };

  const orderByCol = (orderName: string, index: number) => {
    let order: IOrderBy = { sorty_by: orderName, order_by: 'asc' };
    let newOrdering: 'asc' | 'desc' = 'asc';

    if (ordering && ordering === 'asc') {
      newOrdering = 'desc';
    }

    if (ordering && ordering === 'desc') {
      newOrdering = 'asc';
    }

    order.order_by = newOrdering;

    refundService.setOrdering(newOrdering);
    refundService.setOrdered(index);
    refundService.setFilter({ order });
  };

  const headers: IMyTableHeaderIndex[] = [
    { title: 'Pagamento', ordering: true, callback: (index: number) => orderByCol('invoice_payment_date', index) },
    { title: 'Comprador', ordering: true },
    { title: 'Fatura', ordering: false },
    { title: 'Valor', ordering: true, callback: (index: number) => orderByCol('invoice_value', index) },
    { title: 'Solicitado dentro do prazo', ordering: false },
    { title: 'Prazo de resposta', ordering: true, callback: (index: number) => orderByCol('created_at', index) },
    { title: '', ordering: false },
  ];

  if (eduzzAccountsService.hasPermission('myeduzz:requestmanagement:editar')) {
    headers.unshift({
      el: (
        <Checkbox
          indeterminate={qtRefundChecked > 0 && qtRefund !== qtRefundChecked}
          checked={qtRefund === qtRefundChecked}
          style={{ height: 'auto', width: 'auto' }}
          onClick={qtRefund === qtRefundChecked ? onUnCheckAll : onCheckAll}
        />
      ),
      ordering: false,
    });
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <div className={classes.content}>
      <MyTable responsive>
        <MyTableHeader index={headers} ordering={ordering} ordered={ordered} />
        <MyTableLoading colSpan={headers.length} />
        <MyTableBody>
          <MyTableEmpty data={list} colSpan={headers.length} />
          {!isLoading &&
            list?.map((refund: IRefund, index) => {
              const dateToDisplayWarning = moment(refund.created_at).add(4, 'days');
              const isExpiring = moment().isSameOrAfter(dateToDisplayWarning, 'date');

              return (
                <MyTableRow key={`order-${refund.id}`}>
                  <PermissionHide action='myeduzz:requestmanagement:editar'>
                    <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 6, lg: 3 }}>
                      <Checkbox
                        id={`refund-request-checkbox-${index}`}
                        checked={refund.checked === true}
                        style={{ height: 38, width: 'auto' }}
                        onClick={() => onCheck(refund.id)}
                      />
                    </MyTableCol>
                  </PermissionHide>
                  <MyTableCol screenSizes={{ xs: 4, sm: 8, md: 6, lg: 3 }} mobileOrder={2}>
                    {moment(refund.invoice_payment_date).format('DD/MM/YYYY')}
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 10, sm: 10, md: 6, lg: 6 }} mobileOrder={1}>
                    {refund.buyer_name}
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 4, sm: 4, md: 6, lg: 3 }} mobileOrder={3}>
                    {refund.invoice_id}
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 4, sm: 0, md: 6, lg: 6 }} mobileOrder={4}>
                    <FormattedNumber format='BRL' value={refund.invoice_value} />
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 0, sm: 0, md: 1, lg: 1 }}>
                    {refund.requested_after_deadline ? <p className={classes.no}>Não</p> : <p className={classes.yes}>Sim</p>}
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 6, sm: 0, md: 6, lg: 3 }} mobileOrder={6}>
                    <StatusTooltip isExpired={isExpiring} isRequestedAfterDeadline={refund.requested_after_deadline}>
                      <div className={isExpiring ? classes.expired : null}>
                        {isExpiring && <ScheduleIcon style={{ fontSize: 20, marginRight: 4 }} />}
                        {moment(refund.due_date).format('DD/MM/YYYY')}
                      </div>
                    </StatusTooltip>
                  </MyTableCol>
                  <MyTableCol screenSizes={{ xs: 6, sm: 4, md: 3, lg: 3 }} mobileOrder={7} action numeric>
                    <ButtonBase onClick={(e) => handleClick(e, refund)}>
                      <Icon id={`more-icon-${index}`}>more_horiz</Icon>
                    </ButtonBase>
                  </MyTableCol>
                </MyTableRow>
              );
            })}
        </MyTableBody>
      </MyTable>

      {!isLoading && pagination && (
        <MyTablePagination
          onPageChange={onChangePage}
          rowsPerPage={pagination?.per_page || 0}
          count={pagination?.total_items || 0}
          page={pagination?.current_page - 1 || 0}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          labelRowsPerPage='Itens por página:'
          onChangeRowsPerPage={onChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      )}

      <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} onExited={handleClose}>
        <MenuItem onClick={openDetails}>
          <ListAltSharpIcon className={classes.icon} />
          Detalhes
        </MenuItem>
        <Divider />

        <PermissionHide action='myeduzz:requestmanagement:editar'>
          <MenuItem className={classes.approve} onClick={openApprove}>
            <ThumbUpAltSharpIcon color='secondary' className={classes.icon} />
            Aprovar
          </MenuItem>
          <Divider />
          <MenuItem className={classes.approve} onClick={openReproveMotive}>
            <ThumbDownAltSharpIcon color='error' className={classes.icon} />
            Reprovar
          </MenuItem>
        </PermissionHide>
      </Popover>
    </div>
  );
};
export default RefundList;

interface IStatusTooltip {
  isExpired: boolean;
  isRequestedAfterDeadline: boolean;
  children: React.ReactNode;
}

const StatusTooltip = (props: IStatusTooltip) => {
  const { isExpired, isRequestedAfterDeadline, children } = props;

  if (!isExpired) return <>{children}</>;

  const tooltipText: string = isRequestedAfterDeadline
    ? 'Após esse prazo o reembolso será cancelado automaticamente'
    : 'Após esse prazo o reembolso será realizado automaticamente';

  return (
    <Tooltip title={tooltipText}>
      <div>{children}</div>
    </Tooltip>
  );
};
